import React from "react";
import { useTranslation } from "../../../localization/useTranslation";
import { SelectableSecurityMeasure } from "../../../processingActivity/ProcessingActivity.types";
import DotLegalMultiSelectWithButton from "../dotLegalSelectWithButton/DotLegalMultiSelectWithButton";
import DotLegalStepHeader from "../dotLegalStepHeader/DotLegalStepHeader";
import { SecurityMeasureModel } from "./DotLegalSecurityMeasure.types";
import DotLegalSecurityPeriodMet from "../dotLegalSecurityPeriodMet/DotLegalSecurityPeriodMet";
import { Typography } from "@mui/material";
import { useDotLegalSecurityMeasureStyles } from "./DotLegalSecurityMeasure.styles";
import { Color } from "@dotlegal/dotlegal-ui-components";

export interface IDotLegalSecurityMeasure {
    header?: string;
    isLoading: boolean;
    securityMeasureOptions: Array<SelectableSecurityMeasure>;
    securityMeasures: Array<SecurityMeasureModel>;
    onSecurityMeasureChange: (securityMeasures: Array<SecurityMeasureModel>) => void;
    canManageMasterData: boolean;
    setTypedSecurityMeasure: (value: string) => void;
    typedSecurityMeasure: string;
    setShowAddNewSecurityMeasure: (value: boolean) => void;
    tooltip?: string;
    disabled?: boolean;
    errorText?: string;
}

export default function DotLegalSecurityMeasure(props: IDotLegalSecurityMeasure) {
    const { translateString } = useTranslation();
    const styles = useDotLegalSecurityMeasureStyles();

    const onSecurityMeasuresChanged = (securityMeasureIds: Array<string>) => {
        const updated = props.securityMeasures.filter((s) => securityMeasureIds.some((n) => s.securityMeasureId === n));
        let existingSecurityMeasureIds = props.securityMeasures.map((sm) => sm.securityMeasureId);
        const added: Array<SecurityMeasureModel> = securityMeasureIds
            .filter((s) => !existingSecurityMeasureIds.some((e) => e === s))
            .map((s) => {
                return { securityMeasureId: s, isMet: true, derivedFromSystem: [], isGlobal: false };
            });

        props.onSecurityMeasureChange(updated.concat(added));
    };

    const GetSecurityMeasureContent = (model: SelectableSecurityMeasure) => {
        let securityMeasure = props.securityMeasureOptions!.find((d) => d.id === model.id);

        if (securityMeasure === undefined) return undefined;

        return (
            <div key={model.id}>
                <DotLegalSecurityPeriodMet
                    description={securityMeasure!.description}
                    name={securityMeasure!.name}
                    onchange={(value) => handleOnSecurityMeasuresMetChange(model.id, value)}
                    isMet={model.isMet}
                    disabled={props.disabled}
                />
            </div>
        );
    };

    const handleOnSecurityMeasuresMetChange = (id: string, isMet: boolean) => {
        props.securityMeasures!.find((r) => r.securityMeasureId === id)!.isMet = isMet;
        props.onSecurityMeasureChange([...props.securityMeasures!]);
    };

    const showSecurityMeasureContent = () => {
        return props.securityMeasureOptions && (getInheritedSecurityMeasures().length > 0 || getSelectedSecurityMeasures().length > 0);
    };

    function getDisplayedSecurityMeasureIds(): Array<string> {
        if (!props.securityMeasures || !props.securityMeasureOptions) {
            return [];
        }

        return props.securityMeasureOptions
            .filter((option) => option.isInherited || props.securityMeasures.some((sm) => sm.securityMeasureId === option.id))
            .map((option) => option.id);
    }

    function getSelectedSecurityMeasures(): Array<SelectableSecurityMeasure> {
        if (!props.securityMeasures || !props.securityMeasureOptions) {
            return [];
        }

        return props.securityMeasureOptions
            .filter((option) => !option.isInherited && props.securityMeasures.some((sm) => sm.securityMeasureId === option.id))
            .map((option) => ({
                ...option,
                isMet: props.securityMeasures.find((sm) => sm.securityMeasureId === option.id)?.isMet ?? true,
            }));
    }

    function getInheritedSecurityMeasures(): Array<SelectableSecurityMeasure> {
        return props.securityMeasureOptions?.filter((option) => option.isInherited) ?? [];
    }

    function getOptions() {
        let options: Array<any> = [];
        props.securityMeasureOptions?.forEach((x) => {
            if (x.isInherited) {
                options.push({
                    id: x.id,
                    name: x.name,
                    color: Color.lavender,
                    disabled: true,
                    hoverText: x.isGlobal
                        ? translateString("globalSecurityMeasure")
                        : translateString("securityMeasureDerivesFromSystems", { systems: x.derivedFromSystems?.join(", ") }),
                    unDeletable: true,
                });
            } else {
                options.push({ id: x.id, name: x.name });
            }
        });
        return options;
    }

    return (
        <React.Fragment>
            {props.header && <Typography sx={styles.header}>{props.header}</Typography>}

            <DotLegalMultiSelectWithButton
                label={translateString("securityMeasures")}
                placeholder={translateString("securityMeasures")}
                isLoading={props.isLoading}
                options={getOptions()}
                chosenOptions={getDisplayedSecurityMeasureIds()}
                onChange={(securityMeasure) => onSecurityMeasuresChanged(securityMeasure)}
                toolTipText={props.tooltip ?? translateString("securitymeasuresHelperText")}
                onButtonClick={() => props.setShowAddNewSecurityMeasure(true)}
                buttonLabel={translateString("createNew")}
                hasPermission={props.canManageMasterData}
                onSearchValueChange={props.setTypedSecurityMeasure}
                searchValue={props.typedSecurityMeasure}
                disabled={props.disabled}
                noOptionsLabel={translateString("noOptions")}
                errorText={props.errorText}
            />

            {showSecurityMeasureContent() && (
                <React.Fragment>
                    <DotLegalStepHeader toolTipText={translateString("securityImplementationHelperText")} record={false} topMargin>
                        {translateString("securityMeasuresMetHeader")}
                    </DotLegalStepHeader>
                    <div>
                        {getInheritedSecurityMeasures().map((g, index) => {
                            return (
                                <div key={g.id}>
                                    <DotLegalSecurityPeriodMet description={g.description} name={g.name} isMet={g.isMet} disabled />
                                </div>
                            );
                        })}
                        {getSelectedSecurityMeasures().map((r) => {
                            return GetSecurityMeasureContent(r);
                        })}
                    </div>
                </React.Fragment>
            )}
        </React.Fragment>
    );
}
